import { useSearchParams } from "react-router";
import { z } from "zod";
import { RFQStatusSchema, type RFQListResponse, type RFQStatus } from "../../../clients/Purchaser/RFQ";
import { CircleDashed, ArchiveX, CircleCheckBig } from "lucide-react";
import { useCallback, type ReactNode } from "react";
import CircleProgress from "../../Common/CustomIcons/CircleProgress";
import { Capitalized } from "../../../util/formatting";

export type RFQStatusMetadata = {
    title: string;
    icon: ReactNode;
    colorIcon: ReactNode;
    bgClass: string;
    borderClass: string;
};

export const RFQStatusMeta = {
    prettyName(status: RFQStatus | undefined) {
        return (status ?? "")
            .split("_")
            .map(str => Capitalized(str))
            .join(" ");
    },
    properties(status: RFQStatus): RFQStatusMetadata {
        const title = this.prettyName(status);
        switch (status) {
            case "IN_PROGRESS":
                return {
                    title,
                    icon: <CircleProgress.Stone />,
                    colorIcon: <CircleProgress.Bronze />,
                    bgClass: "bg-bronze-100",
                    borderClass: "border border-bronze-100 hover:border-bronze-200"
                };
            case "DRAFT":
                return {
                    title,
                    icon: <CircleDashed className="h-4 w-4 stroke-stone-500" />,
                    colorIcon: <CircleDashed className="h-4 w-4 stroke-stone-500" />,
                    bgClass: "bg-stone-100",
                    borderClass: "border border-stone-100 hover:border-stone-200"
                };
            case "CLOSED":
                return {
                    title,
                    icon: <CircleCheckBig className="h-4 w-4 stroke-green-700" />,
                    colorIcon: <CircleCheckBig className="h-4 w-4 stroke-green-700" />,
                    bgClass: "bg-green-50",
                    borderClass: "border border-green-50 hover:border-green-200"
                };
            case "ARCHIVED":
                return {
                    title,
                    icon: <ArchiveX className="h-4 w-4 text-stone-400" />,
                    colorIcon: <ArchiveX className="h-4 w-4 text-stone-400" />,
                    bgClass: "bg-stone-100",
                    borderClass: "border border-stone-100 hover:border-bronze-200"
                };
        }
    }
};

export const RFQSortSchema = z.enum(["UpdatedDate", "CreatedDate", "AssignedTo", "CreatedBy", "QuoteCount", "ID"]);
export const RFQSortOrderSchema = z.enum(["asc", "desc"]);
export type RFQSort = z.infer<typeof RFQSortSchema>;
export type RFQSortOrder = z.infer<typeof RFQSortOrderSchema>;

// This is the master schema for all filters, search, sort, etc
// It ensures exactly what is in the query params

// -----
// If you need another param, add it to the schema and you will
// automatically be able to set it with setFilters, and retrieve it with filters
// -----
const FilterSchema = z.object({
    // These are not technically "filters" but need to be stored in the search params
    search: z.string().optional(),
    sort: z
        .string()
        .transform(val => (RFQSortSchema.safeParse(val).success ? RFQSortSchema.parse(val) : undefined))
        .optional(),
    sortOrder: z
        .string()
        .transform(val => (RFQSortOrderSchema.safeParse(val).success ? RFQSortOrderSchema.parse(val) : undefined))
        .optional(),

    // Everything below represents a "filter"
    unread: z
        .string()
        .transform(val => (val === "true" ? true : undefined))
        .optional(),
    quotes: z
        .string()
        .transform(val => (val === "true" ? true : undefined))
        .optional(),
    statuses: z
        .string()
        .transform(val =>
            val
                .split(",")
                .map(s => RFQStatusSchema.safeParse(s))
                .filter(result => result.success)
                .map(result => result.data)
        )
        .optional(),
    assignedTo: z
        .string()
        .transform(val =>
            val
                .split(",")
                .map(Number)
                .filter(n => !isNaN(n))
        )
        .optional(),
    createdBy: z
        .string()
        .transform(val =>
            val
                .split(",")
                .map(Number)
                .filter(n => !isNaN(n))
        )
        .optional(),
    updatedStart: z
        .string()
        .transform(val => Number(val))
        .optional(),
    updatedEnd: z
        .string()
        .transform(val => Number(val))
        .optional(),
    createdStart: z
        .string()
        .transform(val => Number(val))
        .optional(),
    createdEnd: z
        .string()
        .transform(val => Number(val))
        .optional(),
    labels: z
        .string()
        .transform(val =>
            val
                .split(",")
                .map(Number)
                .filter(n => !isNaN(n))
        )
        .optional(),
    labelGroups: z
        .string()
        .transform(val =>
            val
                .split(",")
                .map(Number)
                .filter(n => !isNaN(n))
        )
        .optional(),
    vendorRelationships: z
        .string()
        .transform(val =>
            val
                .split(",")
                .map(Number)
                .filter(n => !isNaN(n))
        )
        .optional(),
});

export type RFQFilterParams = z.infer<typeof FilterSchema>;

export function parseRFQFilterParams(searchParams: URLSearchParams): RFQFilterParams {
    // The following operations is what's behind reliably constructing and manipulating filters in a way that
    // gives us guaranteed and predictable values

    // Get all the keys of the FilterSchema
    const filterKeys = Object.keys(FilterSchema.shape);

    // Get all search params for each key as [key, val]
    const searchParamKeyVal = filterKeys.map(key => [key, searchParams.get(key)]);

    // Filter out any values not set
    const searchParamObjectEntries = searchParamKeyVal.filter(([_, value]) => value !== null);

    // Make it an object that looks like our FilterSchema
    const searchParamObject = Object.fromEntries(searchParamObjectEntries);

    // Finally parse it and get a validated filter out of it
    const filters = FilterSchema.parse(searchParamObject);

    return filters;
}

export function useRFQListFilters() {
    const [searchParams, setSearchParams] = useSearchParams();
    const filters = parseRFQFilterParams(searchParams);

    // Since this is created every time it needs to be wrapped in useCallback with setSearchParams
    // as a dependency so it doesn't cause re-renders for no reason
    const setFilters = useCallback(
        (newFilters: Partial<RFQFilterParams>) => {
            setSearchParams(prev => {
                const params = new URLSearchParams(prev);

                // Looping over specifically entries here because that means if they were not passed in to newFilters
                // the existing search param will stay. To unset a filter you have to explicitly do setFilters({ field: undefined })
                Object.entries(newFilters).forEach(([key, value]) => {
                    // if no value was specified, delete it from the params
                    if (value === undefined) {
                        params.delete(key);
                    } else if (Array.isArray(value)) {
                        // if it's an array join it into a single comma joined string
                        value.length ? params.set(key, value.join(",")) : params.delete(key);
                    } else {
                        params.set(key, String(value));
                    }
                });

                return params;
            }, { replace: true });
        },
        [setSearchParams]
    );

    const clearFilters = () => {
        setSearchParams(new URLSearchParams(), { replace: true });
    };

    const getActiveFilterCount = () => {
        let count = 0;
        if (filters.unread === true) count += 1;
        if (filters.quotes === true) count += 1;
        if (filters.statuses) count += 1;
        if (filters.assignedTo) count += 1;
        if (filters.createdBy) count += 1;
        if (filters.updatedStart || filters.updatedEnd) count += 1;
        if (filters.createdStart || filters.createdEnd) count += 1;
        if (filters.labels) count += 1;
        return count;
    };

    return { filters, setFilters, clearFilters, getActiveFilterCount };
}
